/**
 * Assignment status derivation.
 * Ported from crew-app v1 — pure function, no framework dependency.
 */
import { msg } from '@lingui/core/macro'

export type AssignmentStatusSlug =
  | 'attention'
  | 'confirmed'
  | 'inProgress'
  | 'completed'
  | 'cancelled'

export type AssignmentStatusResult = {
  slug: AssignmentStatusSlug
  text: string
  needsAcknowledgement: boolean
  inProgress: boolean
}

type AssignmentInput = {
  active?: boolean | null
  acknowledged?: boolean | null
  route?: {
    active?: boolean | null
    status?: string | null
    waypoints?: Array<{
      order?: { status?: string | null } | null
    }> | null
  } | null
}

export const assignmentStatus = (
  assignment: AssignmentInput,
): AssignmentStatusResult => {
  const { route } = assignment
  const needsAcknowledgement = !assignment.acknowledged

  const hasIncompleteOrders = (route?.waypoints || []).some(
    (wp) => wp?.order?.status === 'confirmed',
  )

  // A work ticket has been turned in for at least one order on the route.
  const hasSubmittedTicket = (route?.waypoints || []).some(
    (wp) => wp?.order?.status === 'turned_in',
  )

  const inProgress =
    assignment.active === true &&
    route?.active === true &&
    (route?.status === 'inProgress' ||
      (route?.status === 'completed' && hasIncompleteOrders))

  const slug = resolveSlug({
    assignment,
    route,
    inProgress,
    needsAcknowledgement,
  })

  const text = resolveText({
    slug,
    inProgress,
    hasIncompleteOrders,
    hasSubmittedTicket,
  })

  return { slug, text, needsAcknowledgement, inProgress }
}

function resolveSlug({
  assignment,
  route,
  inProgress,
  needsAcknowledgement,
}: {
  assignment: AssignmentInput
  route: AssignmentInput['route']
  inProgress: boolean
  needsAcknowledgement: boolean
}): AssignmentStatusSlug {
  const isCancelled =
    assignment.active === false ||
    route?.active === false ||
    route?.status === 'cancelled'

  if (isCancelled) return 'cancelled'
  if (inProgress) return 'inProgress'
  if (route?.status === 'completed') return 'completed'
  if (needsAcknowledgement) return 'attention'
  return 'confirmed'
}

const STATUS_TEXT: Record<AssignmentStatusSlug, string> = {
  cancelled: 'Cancelled',
  inProgress: 'In Progress',
  completed: 'Completed',
  attention: 'Please Acknowledge',
  confirmed: 'Confirmed',
}

function resolveText({
  slug,
  inProgress,
  hasIncompleteOrders,
  hasSubmittedTicket,
}: {
  slug: AssignmentStatusSlug
  inProgress: boolean
  hasIncompleteOrders: boolean
  hasSubmittedTicket: boolean
}): string {
  // Still waiting on the operator to turn a ticket in.
  if (hasIncompleteOrders && inProgress) return 'Awaiting Work Ticket'
  // Ticket(s) turned in but the route isn't finished/reviewed yet — surface
  // that instead of a generic "In Progress".
  if (inProgress && hasSubmittedTicket) return 'Ticket Submitted'
  return STATUS_TEXT[slug]
}

/**
 * Translate status text returned by `assignmentStatus()`.
 * Accepts the lingui `_` function so the helper stays framework-agnostic.
 */
export const translateStatusText = (
  text: string,
  _: (descriptor: { id: string }) => string,
): string => {
  const map: Record<string, () => string> = {
    'Awaiting Work Ticket': () => _(msg`Awaiting Work Ticket`),
    'Ticket Submitted': () => _(msg`Ticket Submitted`),
    'In Progress': () => _(msg`In Progress`),
    Completed: () => _(msg`Completed`),
    'Please Acknowledge': () => _(msg`Please Acknowledge`),
    Cancelled: () => _(msg`Cancelled`),
    Confirmed: () => _(msg`Confirmed`),
  }
  return map[text]?.() ?? text
}
